import React, { Suspense, useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import styles from "../Location.module.scss";
import FreeItem from "../../../components/Home/subComponents/FreeItem";
import NewsletterForm from "../NewsletterForm";
import {
  GOOGLE_API_KEY,
  GOOGLE_MAP_DEFAULT_ZOOM,
  PICK_UP_ORDER_TYPE,
  REACT_APP_CLOUD_FRONT,
  ROUTE_LOCATIONS,
  ROUTE_MENU,
} from "../../../constants";
import NewFooter from "../../../components/Footer/NewFooter";
import { ArrowTiltIcon } from "../../../images/svgIcons/svg";
import { useHistory, useLocation, useParams } from "react-router";
import useMarketLocations from "../../../react-query-hooks/useLocations";
import { formatTime12Hour, replaceSpace } from "../../../helpers/helperMethods";
import Spinner from "../../../components/Spinner/Spinner";
import useCheckMobileScreen from "../../../hooks/useCheckMobileScreen";
import { handleIsLiveLocationRedirection } from "../../../helpers/checkout/LocationHelper";
import { useAppDispatch, useAppSelector } from "../../../redux/store/store";
import {
  updateIsOrderTypeSelected,
  updateOrderDetails,
  updateOrderType,
} from "../../../redux/slices/cartSlice";
import { setLocation, useStoreLocation } from "../../../context/StoreLocation";
import { Helmet } from "react-helmet";
import LocationReviews from "./LocationReviews";
import LocationGridSection from "./LocationGridSection";
import Page404 from "../../../components/404page/Page404";
import { slugifyLocationName } from "../slugifyLocations";
import GoogleMapReact from "google-map-react";
import { MapMarker } from "../../../components/OrderTrackingMap";
import { isSafari } from "react-device-detect";
import cateringBowlImage from "../../../images/caterbowl1.png";
import floatingTacImage from "../../../images/FloatingTAC_sky.png";
import dashGrilCheeseImage from "../../../images/DashGrilledCheese.png";
import DOMPurify from 'dompurify';
import { resetLocationFlow } from "../../../redux/slices/itemSlice";

const LocationImage = React.lazy(() => import("./LocationImage"));

interface RouteParams {
  id?: string;
}

const LocationDetails = () => {
  const { id: locationName } = useParams<RouteParams>();
  const { data: locations, isLoading, refetch, isError } = useMarketLocations();
  const cartDispatch = useAppDispatch();
  const { dispatch } = useStoreLocation();
  const history = useHistory();
  const isItMobile = useCheckMobileScreen();
  const LocationFlow = useAppSelector(
    (state) => state.Items.LocationFlow
  );

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const LocationFlowFromParams = Object.fromEntries(queryParams.entries());

  const item = locations?.find(
    (element: { name: string }) =>
      slugifyLocationName(element.name) == locationName
  ) || {};

  useEffect(() => {
    if(locations && !item?.name) {
      history.push({
        pathname: ROUTE_LOCATIONS
      });
    }
  }, [locations]);

  useEffect(() => {
    if(!item?.name) {
      return
    };
    // Function to handle anchor tag clicks
    const handleAnchorClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if the clicked element is an <a> tag with `data-action="open-modal"`
      if (target.tagName === "A" && target.getAttribute("data-action") === "open-modal") {
        event.preventDefault(); // Prevent the default link behavior

        // Extract latitude and longitude
        const lat = item?.lat;
        const lng = item?.long;
        if (lat && lng) {
          const isAndroid = /Android/i.test(navigator.userAgent);
          const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

          // Open map in the appropriate app
          const address = encodeURIComponent(`Cafe Zupas, ${item?.state}, ${item?.city}, ${item?.address}`);
          if (isAndroid) {
            window.open(`geo:${lat},${lng}?q=${address}`, "_blank");
          } else if (isIOS) {
            window.open(`maps://?q=${address}&ll=${lat},${lng}`, "_blank");
          } else {
            // Fallback for other devices
            window.open(getMapLink(item), "_blank");
          }
        }
      }
    };
    // Attach event listener
    document.addEventListener("click", handleAnchorClick);

    // Cleanup listener on unmount
    return () => {
      document.removeEventListener("click", handleAnchorClick);
    };
  }, [item]);

  useEffect(() => {
    refetch();
  }, [locationName]);

  const descriptionBody = DOMPurify.sanitize(item?.description_body);
  
  const redirect = (path) => {
    const redirectITemPath = LocationFlowFromParams?.pathname ? LocationFlowFromParams :  LocationFlow?.intendedPath
    if(redirectITemPath){
      cartDispatch(resetLocationFlow());
      history.push(redirectITemPath);
    }else{
      history.push(path)
    }
  }

  const descriptionHeader = item?.description_header;
  const handleSelectLocation = (e: any) => {
    e.preventDefault();
    if (!item?.is_live) {
      handleIsLiveLocationRedirection("", item, isItMobile);
    } else {
      setLocation(dispatch, item);
      cartDispatch(updateIsOrderTypeSelected(true));
      cartDispatch(updateOrderType(PICK_UP_ORDER_TYPE));
      cartDispatch(updateOrderDetails(null));
      redirect(ROUTE_MENU);
    }
  };

  const getMapLink = (item) => {
    const googleMapUrl = `https://maps.google.com/maps?q=Cafe+Zupas,+${item?.state},+${item?.city},+${item?.address}`;
    const appleMapUrl = `http://maps.apple.com/?q=Cafe+Zupas,+${item?.state},+${item?.city},+${item?.address}`;
    return isSafari ? appleMapUrl : googleMapUrl;
  };

  if (isLoading) {
    return (
      <div>
        {" "}
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return <Page404 />;
  }
  return (
    <>
      <Helmet>
        <title>
          {`Café Zupas ${item?.city}, ${item?.state} | Bowls, Soups, Salads & Sandwiches`}
        </title>
        <meta
          name="description"
          content=
          {`Café Zupas in  ${item?.city}, ${item?.state} is your go-to for fresh, flavorful, and house-made protein bowls,
             salads, soups and sandwiches. Order online today!`}
          />
      </Helmet>
    <div className={`pt-156 ${styles.locationDetailsWrapper}`}>
      <Row>
        <Col md={6} className="px-0 order-md-1 order-2">
          <div className={styles.titleSection}>
            <h1 className="font-Visby-cf f-w9 f-s55 f-sm-s32 clr-dark-blue text-start cz-mb-40 cz-mb-sm-20">
              {item?.name}
            </h1>
            <p className="lh-normal f-s20 f-sm-s14 font-trade-gothic  text-start f-w4 cz-mb-40">
              Our chef-crafted menu features house-made soups, salads,
              sandwiches, Good Life bowls, and desserts perfect for every
              occasion -- from lunches with friends, dinners with the family to
              catered office lunches. Stop by the restaurant or order in-app
              today!
            </p>
            <div className="d-flex gap-3">
              <a
                className="btn btn-small f-w4 f-s22 shadow-none py-md-2 px-md-3 f-sm-s14 text-nowrap"
                onClick={handleSelectLocation}
              >
                order now
              </a>
              <a
                className="btn btn-small d-flex align-items-center gap-1 outline justify-content-center btn f-w4 f-s22 shadow-none py-md-2 px-md-3 f-sm-s14 text-nowrap"
                href={getMapLink(item)}
                target="_blank"
              >
                get directions <ArrowTiltIcon />
              </a>
            </div>
          </div>
        </Col>
        <Col md={6} className="px-0 order-md-2 order-1">
          <Suspense fallback={null}>
            <LocationImage item={item?.id} />
          </Suspense>
        </Col>
      </Row>
      <Container fluid className="px-0 pb-5 mb-5">
        <div className={styles.aboutLocationCard}>
          <Row>
            <Col md={8}>
              <h2 className="f-s32 f-sm-s24 f-w7 font-Trade-regular cz-mb-32 text-uppercase lh-normal text-start">
                About this Location
              </h2>
              <div className={styles.locationDetailText}>
                <Row>
                  <Col md={6} className="px-md-3 cz-pb-sm-8">
                    <h6 className="text-uppercase lh-normal f-s20 f-sm-s16 font-trade-gothic text-start clr-dark-blue f-w4 cz-mb-16 cz-mb-sm-8">
                      address
                    </h6>
                    <a
                      href={`https://maps.google.com/maps?q=Cafe+Zupas,+${item?.state},+${item?.city},+${item?.address}`}
                      target="_blank"
                    >
                      <p className="text-uppercase lh-22 f-s20 mb-0 font-trade-gothic f-sm-s16 text-decoration-underline text-start f-w4">
                        {item?.address},
                      </p>
                      <p className="text-uppercase lh-22 f-s20 font-trade-gothic f-sm-s16 text-decoration-underline text-start f-w4">
                        {item?.city}, {item?.state} {item?.zip}
                      </p>
                    </a>
                  </Col>
                  <Col md={6} className="px-md-3">
                    <h6 className="text-uppercase lh-normal ms-md-2 f-s20 f-sm-s16 font-trade-gothic text-start clr-dark-blue f-w4 cz-mb-16 cz-mb-sm-8">
                      Phone Number
                    </h6>
                    {isItMobile ? (
                      <a
                        href={`tel:${item?.phone}`}
                        className="text-uppercase d-block clr-dark-grey lh-normal ms-md-2 f-s16  font-trade-gothic text-decoration-underline f-w4 text-start"
                      >
                        {item?.phone}
                      </a>
                    ) : (
                      <p className="text-uppercase lh-normal ms-md-2 f-s20  font-trade-gothic text-decoration-underline f-w4 text-start">
                        {item?.phone}
                      </p>
                    )}
                  </Col>
                  <Col md={6} className="cz-mt-24 px-md-3">
                    <h6 className="text-uppercase lh-normal f-s20 f-sm-s16 font-trade-gothic text-start clr-dark-blue f-w4">
                      restaurant hours
                    </h6>
                    {item &&
                      item?.in_store_location_hours !== undefined &&
                      item?.in_store_location_hours !== null ? (
                      item?.in_store_location_hours.map((locationHour) => (
                        <div
                          className="d-flex justify-content-between align-items-center cz-mt-16"
                          key={locationHour.id}
                        >
                          <p className="text-capitalize lh-normal f-s20 f-sm-s16 font-trade-bold f-w7 mb-0">
                            <strong>{locationHour.day}</strong>{" "}
                          </p>{" "}
                          <div className="w-50">
                            <p className="lh-normal text-nowrap f-s20 font-trade-gothic f-sm-s16 f-w4 mb-0 text-start">
                              {formatTime12Hour(locationHour.start_time)} -{" "}
                              {formatTime12Hour(locationHour.end_time)}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-start">
                        No restaurant hours available
                      </p>
                    )}
                    <div className="d-flex justify-content-between align-items-center cz-mt-16">
                      <p className="text-capitalize lh-normal f-sm-s16 f-s20 font-trade-bold f-w7 mb-0">
                        <strong>Sunday</strong>{" "}
                      </p>{" "}
                      <div className="w-50">
                        <p className="lh-normal f-s18 font-trade-gothic f-w4 mb-0 text-start">
                          {item?.sunday_timings}
                        </p>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="cz-mt-40 cz-mt-sm-32 px-md-3">
                    <h6 className="text-uppercase lh-normal f-s20 f-sm-s16  ms-md-2 font-trade-gothic text-start clr-dark-blue f-w4">
                      services
                    </h6>
                    <div>
                      <div
                        className={`${styles.servicesWrapper} ${item?.services?.length === 4 &&
                            item?.services?.length === 6
                            ? "justify-content-between"
                            : ""
                          }`}
                      >
                        {item?.services?.map((service) => {
                          return (
                            <>
                              <div key={service.name}>
                                <div className="d-flex flex-column justify-content-center align-items-center">
                                  <img
                                    src={`${REACT_APP_CLOUD_FRONT}/images/Location/features/${replaceSpace(
                                      service.name.toLowerCase(),
                                      "_"
                                    )}.svg`}
                                    alt={service.name}
                                  />
                                  <p className="text-uppercase lh-normal mt-2 f-sm-s12 f-s16 text-center font-trade-gothic  f-w4">
                                    {service.name}
                                  </p>
                                </div>
                              </div>
                            </>
                          );
                        })}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col md={4} className="cz-mt-sm-32">
              <div className={styles.locationDetailMap}>
                <GoogleMapReact
                  bootstrapURLKeys={{ key: GOOGLE_API_KEY }}
                  defaultCenter={{ lat: item?.lat, lng: item?.long }}
                  defaultZoom={GOOGLE_MAP_DEFAULT_ZOOM}
                >
                  <MapMarker
                    lat={item?.lat}
                    lng={item?.long}
                    text={
                      <div className="d-flex gap-1 align-items-center">
                        <img
                          src={`${REACT_APP_CLOUD_FRONT}/images/Location/Location_markup_blue.svg`}
                          width={30}
                        />
                      </div>
                    }
                  ></MapMarker>
                </GoogleMapReact>
              </div>

              <div>
                {item?.avg_google_rating && item?.total_google_reviews && (
                  <LocationReviews
                    googleRating={item?.avg_google_rating}
                    googleReviews={item?.total_google_reviews}
                  />
                )}
              </div>
            </Col>
          </Row>
        </div>
        {
          descriptionBody ? 
          <div className={styles.locationDesCard}>
          <div className="px-md-0 px-4 pt-md-0 pt-3">
          <h2 className="font-Trade-standard-bold lg-normal f-sm-s24 mb-md-5 mb-3 Trade-regular">{descriptionHeader}</h2>
          <div dangerouslySetInnerHTML={{ __html: descriptionBody }} />
          
          </div>
 
           {
             isItMobile ?
               <Col md={4}>
                 <img src={cateringBowlImage} alt="bowl" className="img-fluid" />
               </Col> : <Row>
                 <Col md={4}>
                   <img src={cateringBowlImage} alt="bowl" className="img-fluid" />
                 </Col>
                 <Col md={4}>
                   <img src={floatingTacImage} alt="tac" className="img-fluid" />
                 </Col>
                 <Col md={4}>
                   <img
                     src={dashGrilCheeseImage}
                     alt="cheese"
                     className="img-fluid"
                   />
                 </Col>
               </Row>
           }
 
         </div> : null
        }
   
        <LocationGridSection location={item} />
        <div className="px-md-0 px-4">
          <Col lg="10" md="10" sm="12" xs="12" className="m-auto">
            <FreeItem
              headingText="STAY UPDATED"
              headingStyle="text-uppercase lh-normal"
              content={
                "Nourish the Good Life with exclusive Café Zupas offers, updates and seasonal menus sent straight to your inbox."
              }
            />
          </Col>
          <NewsletterForm />
        </div>
      </Container>
      <NewFooter />
    </div>
    </>
  );
};

export default LocationDetails;
